import { useMemo } from "react";

import { useHomeContext } from "app/pages/activation/context/Home/HomeContext";
import { generateAutomationName } from "app/pages/activation/helpers/generateAutomationName";
import { useActivationMetadataQuery } from "app/pages/activation/queries/data/useActivationMetadataQuery";

import { useCompetitorsTrafficShareWidgetData } from "./hooks/useCompetitorsTrafficShareWidgetData";
import { useRankTrackerWidgetData } from "./hooks/useRankTrackerWidgetData";
import { useShareDataInvalidation } from "./hooks/useShareDataInvalidation";
import { useTrafficWidgetQuery } from "./hooks/useTrafficWidgetQuery";
import * as SC from "./styles";
import { CompetitorsTrafficShare } from "./TrafficWidgets/CompetitorsTrafficShare/CompetitorsTrafficShare";
import { MyTrafficShare } from "./TrafficWidgets/MyTrafficShare/MyTrafficShare";
import { RankTracker } from "./TrafficWidgets/RankTracker/RankTracker";

interface IShareDataSchema {
    onCreateProject(): void;
}

export const ShareData = ({ onCreateProject }: IShareDataSchema) => {
    const { activeActivationId } = useHomeContext();

    useShareDataInvalidation();

    const { isLoading: isMetadataLoading, data: projectsMetadata } = useActivationMetadataQuery();

    const hasProjects = useMemo(
        () => !isMetadataLoading && !!projectsMetadata?.totalNumber,
        [projectsMetadata, isMetadataLoading],
    );

    const isActiveActivationInMetadata = useMemo(() => {
        if (!activeActivationId || !projectsMetadata?.activations?.length) {
            return false;
        }

        return projectsMetadata.activations.some(({ id }) => id === activeActivationId);
    }, [activeActivationId, projectsMetadata]);

    const {
        data: trafficData,
        isLoading: isTrafficLoading,
        isFetching: isTrafficFetching,
        isEmpty: isTrafficEmpty,
    } = useTrafficWidgetQuery(activeActivationId!);

    const {
        shareOfVoiceData,
        isLoading: isCompetitorsLoading,
        isEmpty: isCompetitorsEmpty,
        hasTopics,
    } = useCompetitorsTrafficShareWidgetData(activeActivationId!, isMetadataLoading);

    const {
        data: rankTrackerData,
        isLoading: isRankTrackerLoading,
        isEmpty: isRankTrackerEmpty,
        isScraping,
        isRTAvailable,
    } = useRankTrackerWidgetData(activeActivationId, isMetadataLoading, isCompetitorsLoading, {
        hasProjects,
        isActiveActivationInMetadata,
    });

    return (
        <SC.Container data-automation={generateAutomationName("home", ["share", "data", "container"])}>
            <MyTrafficShare
                onCreateProject={onCreateProject}
                isEmpty={isTrafficEmpty}
                isLocked={false}
                trafficData={trafficData}
                isLoading={isTrafficLoading || isMetadataLoading || isTrafficFetching}
            />

            <CompetitorsTrafficShare
                onCreateProject={onCreateProject}
                isEmpty={isCompetitorsEmpty}
                isLocked={false}
                trafficData={shareOfVoiceData}
                isLoading={isCompetitorsLoading}
                hasProjects={hasProjects}
            />

            {isRTAvailable ? (
                <RankTracker
                    onCreateProject={onCreateProject}
                    isEmpty={isRankTrackerEmpty}
                    isLocked={false}
                    isLoading={isRankTrackerLoading}
                    hasProjects={hasProjects}
                    hasTopics={hasTopics}
                    data={rankTrackerData}
                    isScraping={isScraping}
                />
            ) : null}
        </SC.Container>
    );
};
